import type { Catalogs } from '@/types';

// Etiquetas legibles para cada columna auditada. La columna sentinel
// '__CREATED__' tiene su propia etiqueta (no aparece en cambios — los
// eventos de creacion tienen tipo='creacion' en lugar de 'cambio').
export const COLUMN_LABELS: Record<string, string> = {
  NombreTarea: 'Nombre',
  Descripcion: 'Descripción',
  Observacion: 'Observación',
  IDEstado: 'Estado',
  Responsable: 'Responsable',
  IDAutorizadoINT: 'Autorizador',
  IDSprint: 'Sprint',
  HorasEstimadas: 'Horas estimadas',
  Prioridad: 'Prioridad',
  IDCategoria: 'Categoría',
  IDProyecto: 'Proyecto',
  IDCaso: 'Caso',
  IDLote: 'Lote',
  IDBloque: 'Bloque',
  FechaInicio: 'Fecha inicio',
  FechaProyectada: 'Fecha proyectada',
  FechaCompletado: 'Fecha completado',
  FechaAutorizadoINT: 'Fecha autorizado',
  LinkEntrega: 'Link entrega',
  PesoTarea: 'Peso',
  PorAvance: '% Avance',
  EsActivo: 'Visibilidad',
};

export function labelForColumn(columna: string): string {
  return COLUMN_LABELS[columna] ?? columna;
}

// Color semántico del valor para columnas que lo tienen (Estado, Prioridad).
// Devuelve el hex del catálogo o null si la columna no tiene color asociado
// (el render usa un pill neutro en ese caso).
export function resolveColor(
  columna: string,
  raw: string | null,
  catalogs: Catalogs | undefined,
): string | null {
  if (raw === null || raw === '' || !catalogs) return null;
  const id = Number(raw);
  if (Number.isNaN(id)) return null;
  if (columna === 'IDEstado') {
    return catalogs.statuses.find((x) => x.idEst === id)?.colorHex ?? null;
  }
  if (columna === 'Prioridad') {
    return catalogs.priorities.find((x) => x.id === id)?.colorHex ?? null;
  }
  return null;
}

// Resuelve el valor crudo a algo legible para el usuario. Para FKs busca
// el nombre en el catalogo correspondiente; para fechas trunca el time si
// es 00:00:00; para booleans bit traduce a Activa/Inactiva; para texto
// pasa a través con truncate visual aplicado por el render.
export function resolveValue(
  columna: string,
  raw: string | null,
  catalogs: Catalogs | undefined,
): string {
  if (raw === null || raw === '') return '—';

  // FKs a catálogos
  if (catalogs) {
    if (columna === 'IDEstado') {
      const id = Number(raw);
      const s = catalogs.statuses.find((x) => x.idEst === id);
      return s?.estado ?? raw;
    }
    if (columna === 'Responsable' || columna === 'IDAutorizadoINT') {
      const id = Number(raw);
      const u = catalogs.users.find((x) => x.idCol === id);
      return u?.nombreCompleto || u?.nombre || raw;
    }
    if (columna === 'IDSprint') {
      const id = Number(raw);
      const s = catalogs.sprints.find((x) => x.idSprint === id);
      return s?.nombre ?? raw;
    }
    if (columna === 'IDCategoria') {
      const id = Number(raw);
      const c = catalogs.categories.find((x) => x.idCatTareaArq === id);
      return c?.nombre ?? raw;
    }
    if (columna === 'IDProyecto') {
      const id = Number(raw);
      const p = catalogs.projects.find((x) => x.idProyecto === id);
      return p?.nombre ?? raw;
    }
    if (columna === 'IDCaso') {
      const id = Number(raw);
      const c = catalogs.cases.find((x) => x.idCaso === id);
      return c?.loteCliente ?? `Caso #${id}`;
    }
    if (columna === 'IDLote') {
      const id = Number(raw);
      const l = catalogs.lots.find((x) => x.idLote === id);
      return l?.idBd ?? `Lote #${id}`;
    }
    if (columna === 'IDBloque') {
      const id = Number(raw);
      const b = catalogs.blocks.find((x) => x.idBloque === id);
      return b?.bloque ?? `Bloque #${id}`;
    }
    if (columna === 'Prioridad') {
      const id = Number(raw);
      const p = catalogs.priorities.find((x) => x.id === id);
      return p?.nombre ?? raw;
    }
  }

  // Bit -> Activa / Inactiva (visibilidad de tarea)
  if (columna === 'EsActivo') {
    return raw === '1' || raw.toLowerCase() === 'true' ? 'Activa' : 'Inactiva';
  }

  // Fechas: trunca el time si es medianoche.
  if (columna.startsWith('Fecha')) {
    // Datetime de SQL en formato 'YYYY-MM-DD HH:mm:ss.fff' o ISO con T.
    const isoLike = raw.replace(' ', 'T');
    const d = new Date(isoLike);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return raw;
  }

  // Decimal: limpia ceros sobrantes de la cola (".50" -> ".5").
  if (columna === 'HorasEstimadas' || columna === 'PorAvance') {
    const n = parseFloat(raw);
    if (!isNaN(n)) return String(n);
  }

  return raw;
}
